import React, { useState, useContext } from "react";
import { useTheme } from "@mui/material/styles";
import {
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Avatar,
} from "@mui/material";
import IconButton from "../ui/IconButton";
import { Lock, Logout, Brightness4, Brightness7 } from "@mui/icons-material";
import { AuthContext } from "../../contexts/AuthContext";
import { ECountry, useCountry } from "../../contexts/CountryContext";
import { useIntl } from "../../contexts/IntlContext";
import { useTheme as useThemeContext } from "../../contexts/ThemeContext";
import { removeToken } from "../../services/auth";
import { ChangePasswordDialog } from "../ChangePasswordDialog";
import Select from "../ui/Select";

interface UserMenuProps {
  showTheme?: boolean;
  showCountry?: boolean;
}

export function UserMenu({ showTheme = false, showCountry = false }: UserMenuProps) {
  const { authInfo } = useContext(AuthContext);
  const intl = useIntl();
  const theme = useTheme();
  const { country, setCountry } = useCountry();
  const { isDarkMode, toggleTheme } = useThemeContext();
  const [userMenuAnchorEl, setUserMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);

  const gitSha = process.env.REACT_APP_GIT_SHA?.trim();
  const buildTime = process.env.REACT_APP_BUILD_TIME?.trim();
  const appVersion = process.env.REACT_APP_VERSION?.trim();
  const buildLabelParts = [
    appVersion ? `v${appVersion}` : null,
    gitSha ? `(${gitSha.slice(0, 7)})` : null,
    buildTime ? new Date(buildTime).toLocaleString() : null,
  ].filter(Boolean);
  const buildLabel = buildLabelParts.length ? buildLabelParts.join(" ") : intl.formatMessage({ id: "appBar.build.unknown" });

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setUserMenuAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchorEl(null);
  };

  const handleLogout = () => {
    removeToken();
  };

  const stringAvatar = (name: string | undefined) => {
    if (!name) return 'UU';
    const parts = name.split(' ');
    return parts.length > 1 ? `${parts[0][0]}${parts[1][0]}` : name.substring(0, 2).toUpperCase();
  };

  return (
    <>
      <IconButton
        color="inherit"
        onClick={handleUserMenuOpen}
      >
        <Avatar sx={{
          bgcolor: theme.palette.primary.main,
          transform: 'scale(0.8)',
        }}>
          {stringAvatar(authInfo?.email)}
        </Avatar>
      </IconButton>
      <Menu
        anchorEl={userMenuAnchorEl}
        open={Boolean(userMenuAnchorEl)}
        onClose={handleUserMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem disabled>
          <ListItemText primary={intl.formatMessage({ id: `appBar.role.${authInfo?.role || 'ADMIN'}` })} sx={{ color: 'text.secondary' }} />
        </MenuItem>
        <MenuItem disabled>
          <ListItemText
            primary={intl.formatMessage({ id: "appBar.build" })}
            secondary={buildLabel}
            sx={{ color: "text.secondary" }}
          />
        </MenuItem>
        <Divider />
        {showCountry && (
          <MenuItem disableRipple sx={{ "&:hover": { backgroundColor: "transparent" } }}>
            <Select
              label={intl.formatMessage({ id: "common.country" })}
              value={country}
              onChange={(e) => setCountry(e.target.value as ECountry)}
              onClick={(e) => e.stopPropagation()}
            >
              {Object.values(ECountry).map((c) => (
                <MenuItem key={c} value={c}>
                  {intl.formatMessage({ id: "country." + c })}
                </MenuItem>
              ))}
            </Select>
          </MenuItem>
        )}
        {showTheme && (
          <MenuItem onClick={toggleTheme}>
            <ListItemIcon>
              {isDarkMode ? <Brightness4 fontSize="small" /> : <Brightness7 fontSize="small" />}
            </ListItemIcon>
            <ListItemText primary={intl.formatMessage({ id: `appBar.theme.${isDarkMode ? 'light' : 'dark'}` })} />
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            setChangePasswordOpen(true);
            handleUserMenuClose();
          }}
        >
          <ListItemIcon>
            <Lock fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={intl.formatMessage({ id: 'appBar.changePassword' })} />
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <Logout fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={intl.formatMessage({ id: 'appBar.logout' })} />
        </MenuItem>
      </Menu>
      <ChangePasswordDialog
        open={changePasswordOpen}
        onClose={() => setChangePasswordOpen(false)}
      />
    </>
  );
}
